import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./SwapCurrencyInput.scss";
import { Currency } from "server/walletManage/type";
import { WrapToken } from "server/swap/type";
import { WEB3 } from "modules/web3/Web3";
import {
  formatNumber,
  numberParserForInput,
  unformatNumber,
} from "utils/numberUtil";
import classNames from "classnames";
import { emptyCurrency } from "view/assets/images";
import BigNumber from "bignumber.js";

export type SwapCurrencyInputProps = {
  label: string;
  wrapTokenList: Array<WrapToken & { currency: Currency }>;
  wrapToken?: WrapToken & {
    currency?: Currency;
  };
  onBlur: () => void;
  onSelect: (item: WrapToken & { currency: Currency }) => void;
  amount: string;
  onChangeAmount: (amount: string) => void;
  balance?: string;
  notEnoughBalance?: boolean;
};

function SwapCurrencyInput({ balance = "", ...props }: SwapCurrencyInputProps) {
  const {
    label,
    wrapTokenList,
    wrapToken,
    onBlur,
    onSelect,
    amount,
    onChangeAmount,
    notEnoughBalance
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocus, setIsFocus] = useState<boolean>(false);
  // const [needToSelect, setNeedToSelect] = useState<boolean>(false);
  const [isOpenCandidateCurrency, setIsOpenCandidateCurrency] =
    useState<boolean>(false);

  const needToSelect = useMemo(
    () => wrapTokenList.length > 0 && !wrapToken,
    [wrapTokenList, wrapToken]
  );

  const disabled = useMemo(() => wrapTokenList.length === 0, [wrapTokenList]);
  const disabledInput = useMemo(() => !wrapToken, [wrapToken]);

  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (modalRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpenCandidateCurrency(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [modalRef]);

  const onClickSwapCurrencyInput = useCallback(() => {
    if (disabled) return;
    if (!wrapToken) {
      setIsOpenCandidateCurrency(true);
      return;
    }
    inputRef.current?.focus();
  }, [disabled, wrapToken, inputRef]);

  return (
    <>
      {isOpenCandidateCurrency && (
        <div className="currency-select-modal">
          <div className="currency-select-modal-content" ref={modalRef}>
            <div className="modal-header">Select a currency</div>
            <div className="modal-body">
              {wrapTokenList?.map((wrapTokenElement) => {
                return (
                  <button
                    key={wrapTokenElement?.wrapTokenId}
                    className="currency-item"
                    onClick={() => {
                      onSelect(wrapTokenElement);
                      // setWrapToken(wrapTokenElement);
                      setIsOpenCandidateCurrency(false);
                    }}
                  >
                    <img
                      className="currency-item-icon"
                      src={wrapTokenElement?.currency?.image}
                      alt=""
                    />
                    <span className="currency-item-symbol">
                      {wrapTokenElement?.currency?.unit}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div
        className={classNames("swap-currency-input", { focus: isFocus })}
        onClick={onClickSwapCurrencyInput}
      >
        <span className="swap-currency-input-label">{label}</span>
        <div className="swap-currency-input-top">
          <input
              className={classNames({'not-enough-balance': notEnoughBalance})}
            ref={inputRef}
            placeholder="0"
            value={numberParserForInput(amount)}
            onChange={(e) => {
              onChangeAmount(unformatNumber(e.target.value));
            }}
            onBlur={() => {
              setIsFocus(false);
              onBlur();
            }}
            onFocus={() => {
              setIsFocus(true);
            }}
            disabled={disabled || disabledInput}
          />
          <button
            className={classNames("currency-select", { active: needToSelect })}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpenCandidateCurrency(true);
            }}
          >
            <img
              className="currency-select-icon"
              src={wrapToken?.currency?.image ?? emptyCurrency}
              alt=""
            />
            <span className="currency-select-symbol">{wrapToken?.unit}</span>
          </button>
        </div>
        <div className="swap-currency-input-bottom">
          <span className="value">{/* $- */}</span>
          <span className="balance">
            <span className="balance-label">Balance: </span>
            {formatNumber(WEB3.fromWei(balance))}&nbsp;
            {wrapToken?.unit}
          </span>
        </div>
      </div>
    </>
  );
}

export default SwapCurrencyInput;
